import React, { useState } from "react";
import PublicShell from "../../components/PublicShell";
import api from "../../lib/api";
import { useBrand } from "../../lib/brand";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Mail, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import usePageSeo from "../../lib/seo";

function formatBrPhone(digits) {
  if (!digits) return "";
  const d = String(digits).replace(/\D/g, "");
  if (d.length === 13 && d.startsWith("55")) {
    return `+55 (${d.slice(2, 4)}) ${d.slice(4, 9)}-${d.slice(9)}`;
  }
  return `+${d}`;
}

export default function Contact() {
  usePageSeo({
    title: "Contato",
    description: "Fale com a equipe da Azuhomes: e-mail, WhatsApp e formulário. Atendimento direto, sem intermediários.",
    path: "/contato",
  });
  const brand = useBrand();
  const [f, setF] = useState({ name: "", email: "", phone: "", message: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/public/contact", f);
      setSent(true);
      toast.success("Mensagem enviada. Retornaremos em breve.");
    } catch {
      toast.error("Não foi possível enviar. Tente pelo WhatsApp.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <PublicShell>
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-24 grid md:grid-cols-2 gap-16">
        <div>
          <span className="text-[11px] uppercase tracking-[0.28em] text-[#3B9FD4]">Contato</span>
          <h1 className="font-display text-5xl text-[#0AA89C] mt-3">Fale com a nossa equipe.</h1>
          <p className="text-[#22302E] mt-6 leading-relaxed">
            O atendimento é feito diretamente pela equipe da Azuhomes, de segunda a segunda. Para solicitações
            de reserva com data próxima ou situações urgentes durante a estadia, o WhatsApp é o canal mais
            rápido. Mensagens recebidas por e-mail ou pelo formulário são respondidas em até 24 horas úteis.
          </p>
          <div className="mt-10 space-y-6">
            <a
              data-testid="contact-whatsapp"
              href={`https://wa.me/${brand?.contact_whatsapp || ""}`}
              className="flex items-center gap-4 group"
            >
              <span className="w-12 h-12 rounded-full bg-[#0AA89C]/5 border border-[#0AA89C]/10 flex items-center justify-center text-[#0AA89C]">
                <MessageCircle className="w-5 h-5" />
              </span>
              <div>
                <div className="text-xs uppercase tracking-[0.22em] text-slate-500">WhatsApp</div>
                <div className="text-[#0AA89C] font-medium group-hover:text-[#3B9FD4]">{formatBrPhone(brand?.contact_whatsapp)}</div>
              </div>
            </a>
            <a
              data-testid="contact-email"
              href={`mailto:${brand?.contact_email || ""}`}
              className="flex items-center gap-4 group"
            >
              <span className="w-12 h-12 rounded-full bg-[#0AA89C]/5 border border-[#0AA89C]/10 flex items-center justify-center text-[#0AA89C]">
                <Mail className="w-5 h-5" />
              </span>
              <div>
                <div className="text-xs uppercase tracking-[0.22em] text-slate-500">E-mail</div>
                <div className="text-[#0AA89C] font-medium group-hover:text-[#3B9FD4]">{brand?.contact_email}</div>
              </div>
            </a>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-8">
          {sent ? (
            <div className="text-center py-8">
              <div className="font-display text-3xl text-[#0AA89C]">Mensagem recebida.</div>
              <p className="text-[#22302E] mt-3">Retornaremos em até 24 horas úteis pelo canal informado.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              <div>
                <Label>Nome *</Label>
                <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required data-testid="contact-name" />
              </div>
              <div>
                <Label>E-mail *</Label>
                <Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required data-testid="contact-email-input" />
              </div>
              <div>
                <Label>Telefone</Label>
                <Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="contact-phone" />
              </div>
              <div>
                <Label>Mensagem *</Label>
                <Textarea value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} rows={5} required data-testid="contact-message" />
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-[#0AA89C] hover:bg-[#066B63] text-white" data-testid="contact-submit">
                {loading ? "Enviando…" : "Enviar mensagem"}
              </Button>
            </form>
          )}
        </div>
      </section>
    </PublicShell>
  );
}
